import React, { useState, useEffect } from 'react';
import { Typography, Box, Paper } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getRandomSalutation } from '../constants/salutations';
import axiosInstance from '../utils/axiosConfig';

interface LastLogin {
  lastLogin: string;
}

interface Application {
  _id: string;
  createdAt: string;
}

interface BandRecommendation {
  _id: string;
  createdAt: string;
}

interface UserGreetingProps {
  ticketsSold: number;
}

const getFirstName = (fullName: string): string => {
  return fullName.split(' ')[0];
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

const UserGreeting: React.FC<UserGreetingProps> = ({ ticketsSold }) => {
  const [newApplications, setNewApplications] = useState<number | null>(null);
  const [newBands, setNewBands] = useState<number | null>(null);
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  const [salutation, setSalutation] = useState<string>('');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      const firstName = getFirstName(user.name);
      setSalutation(getRandomSalutation(firstName));
    }
  }, [user]);

  useEffect(() => {
    const fetchLastLogin = async () => {
      if (user && user._id) {
        try {
          const response = await axiosInstance.get<LastLogin>(
            `${API_BASE_URL}/api/users/${user._id}/lastLogin`
          );
          setLastLogin(response.data.lastLogin);
        } catch (error) {
          console.error('Error fetching last login:', error);
        }
      }
    };

    if (user) {
      fetchLastLogin();
    }
  }, [user]);

  useEffect(() => {
    const fetchNewItems = async () => {
      if (!user || !lastLogin) return;

      try {
        const lastLoginDate = new Date(lastLogin);
        const currentTime = new Date();
        
        // If last login was less than 10 seconds ago, assume this is a fresh login
        if ((currentTime.getTime() - lastLoginDate.getTime()) < 10000) {
          setNewApplications(0);
          setNewBands(0);
          return;
        }

        // Fetch applications and bands created after last login
        const [banBamResponse, navigatorResponse, bandsResponse] = await Promise.all([
          axiosInstance.get<Application[]>(`${API_BASE_URL}/api/banbam`, {
            params: {
              createdAt_gte: lastLoginDate.toISOString(),
              timestamp: new Date().getTime() // Cache buster
            }
          }),
          axiosInstance.get<Application[]>(`${API_BASE_URL}/api/navigator`, {
            params: {
              createdAt_gte: lastLoginDate.toISOString(),
              timestamp: new Date().getTime() // Cache buster
            }
          }),
          axiosInstance.get<BandRecommendation[]>(`${API_BASE_URL}/api/bandrecommendations`, {
            params: {
              createdAt_gte: lastLoginDate.toISOString(),
              timestamp: new Date().getTime() // Cache buster
            }
          })
        ]);

        // Filter out items that might have the same timestamp as last login
        const filterByDate = (item: { createdAt: string }) => 
          new Date(item.createdAt).getTime() > lastLoginDate.getTime();

        const newApps = [
          ...banBamResponse.data.filter(filterByDate),
          ...navigatorResponse.data.filter(filterByDate)
        ].length;

        const newBandsCount = bandsResponse.data.filter(filterByDate).length;

        setNewApplications(newApps);
        setNewBands(newBandsCount);

      } catch (error) {
        console.error('Error fetching new items:', error);
        setNewApplications(null);
        setNewBands(null);
      }
    };

    if (user && lastLogin) {
      fetchNewItems();
    }
  }, [user, lastLogin]);

  if (!user) {
    return null;
  }

  const getRelativeTime = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);
  
    if (diffInSeconds < 60) return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  };
  

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2.5, 
        mb: 2.5, 
        bgcolor: '#455e72',
        minHeight: '158px',
        maxHeight: '158px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3  // This line controls the curvature
      }}
    >
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Main greeting - with text overflow handling */}
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 1.5,
            fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' },
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {salutation}
        </Typography>
        
        {/* Last login info - more compact */}
        {lastLogin && (
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1.5, 
              opacity: 0.9,
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            Your last login was on: {new Date(lastLogin).toLocaleString()} ({getRelativeTime(lastLogin)})
          </Typography>
        )}
        
        {/* Status items - use flexbox for horizontal layout on larger screens */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: { xs: 1, md: 2 },
          '& > *': { flex: 1 }
        }}>
          {/* Applications status */}
          {newApplications !== null && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: newApplications > 0 ? '#4caf50' : 'inherit',
                py: 0.25,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {newApplications > 0 
                ? `${newApplications} new application${newApplications === 1 ? '' : 's'} since last login`
                : 'No new applications since last login'}
            </Typography>
          )}
          
          {/* Bands status */}
          {newBands !== null && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: newBands > 0 ? '#4caf50' : 'inherit',
                py: 0.25,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {newBands > 0 
                ? `${newBands} new band${newBands === 1 ? '' : 's'} added since last login`
                : 'No new bands added since last login'}
            </Typography>
          )}
          
          {/* Tickets status */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              color: ticketsSold > 0 ? '#4caf50' : 'inherit',
              py: 0.25,
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {ticketsSold > 0 
              ? `${ticketsSold} ticket${ticketsSold === 1 ? '' : 's'} sold since last login`
              : 'No tickets sold since last login'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserGreeting;